import React, { useState } from "react";
import { memberFetch } from "../../lib/memberApi";
import { toast } from "sonner";
import { X, Calendar, Send } from "lucide-react";

const MODES = [
  { id: "in_person", label: "In-Person" },
  { id: "zoom", label: "Zoom" },
  { id: "either", label: "Either Works" },
];

export default function RequestSessionModal({ onClose, onSent }) {
  const [mode, setMode] = useState("either");
  const [slots, setSlots] = useState(["", "", ""]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const updateSlot = (i, v) => {
    const copy = [...slots];
    copy[i] = v;
    setSlots(copy);
  };

  const submit = async (e) => {
    e.preventDefault();
    const clean = slots.map((s) => s.trim()).filter(Boolean);
    if (clean.length === 0) {
      toast.error("Add at least one day/time preference");
      return;
    }
    setBusy(true);
    try {
      await memberFetch("/member/session-request", {
        method: "POST",
        body: JSON.stringify({ mode, slot_options: clean, note: note.trim() || null }),
      });
      toast.success("Request sent · Davon will reach out soon");
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(err.message || "Couldn't send request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="request-session-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <form onSubmit={submit} className="w-full sm:max-w-lg border border-[#E5B43A] bg-[#0F0F0F] shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5B43A]/40 bg-[#E5B43A]/10 flex items-center gap-3">
          <Calendar className="text-[#E5B43A] shrink-0" size={18} />
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] font-bold flex-1">Request Training Session</div>
          <button type="button" onClick={onClose} disabled={busy} className="text-zinc-400 hover:text-white" data-testid="request-session-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Format</label>
            <div className="grid grid-cols-3 gap-2">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  data-testid={`request-mode-${m.id}`}
                  className={`px-3 py-2.5 text-xs font-bold uppercase tracking-widest border transition-colors ${mode === m.id ? "border-[#E5B43A] bg-[#E5B43A] text-black" : "border-zinc-700 text-zinc-300 hover:border-[#E5B43A]"}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Preferred Day &amp; Time (Pick 1-3)</label>
            <div className="space-y-2">
              {slots.map((s, i) => (
                <input
                  key={`slot-${i}`}
                  type="text"
                  value={s}
                  onChange={(e) => updateSlot(i, e.target.value)}
                  placeholder={["e.g. Tuesday 6:30 AM", "Backup: Thursday 5 PM", "Backup: Saturday 10 AM"][i]}
                  data-testid={`request-slot-${i}`}
                  className="w-full bg-[#0A0A0A] border border-zinc-800 px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#E5B43A]"
                />
              ))}
            </div>
            <div className="text-[10px] tracking-widest uppercase text-zinc-600 mt-2">Davon will lock one in &amp; text/email you to confirm.</div>
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Note <span className="text-zinc-700">(optional)</span></label>
            <textarea
              rows={3}
              maxLength={400}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything Davon should know — focus area, recent injury, goal..."
              data-testid="request-note"
              className="w-full bg-[#0A0A0A] border border-zinc-800 px-4 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#E5B43A] resize-none"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-zinc-800 bg-[#0A0A0A] flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button type="button" onClick={onClose} disabled={busy} className="px-5 py-3 text-xs font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:border-white hover:text-white transition-colors disabled:opacity-50">Cancel</button>
          <button type="submit" disabled={busy} data-testid="request-session-submit" className="inline-flex items-center justify-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-widest bg-[#E5B43A] text-black hover:bg-white transition-colors disabled:opacity-60">
            <Send size={13} /> {busy ? "Sending..." : "Send Request"}
          </button>
        </div>
      </form>
    </div>
  );
}
